import { COCKPIT_RULES, MONTHS_FULL, consolidate, lastDay, meur, pct } from '@cockpit/shared';
import { Card, ErrorState, Notice, SectionHeader, SourceTag, Spinner, Table, Td, Th, cn } from '@cockpit/ui';
import { ArrowLeft, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { HBars } from '../components/charts.js';
import { PageHeader } from '../components/Layout.js';
import { useCockpit } from '../lib/queries.js';

export function ConsolidePage() {
  const q = useCockpit();
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorState>{(q.error as Error).message}</ErrorState>;
  const d = q.data!;
  const rows = consolidate(d.order, d.data);
  const months = Math.max(0, ...d.order.map((k) => d.data[k]?.monthsAvail.length ?? 0));
  const names = rows.map((r) => r.name).join(', ');
  const fin = d.freshness.fin?.split('-').reverse().join('/');
  const ope = d.freshness.ope?.split('-').reverse().join('/');

  return (
    <div className="flex flex-col gap-10">
      <div>
        <PageHeader
          eyebrow="Cockpit mensuel"
          title="Vue consolidée"
          sub={`Comparaison des ${rows.length} entités suivies, cumulé au ${lastDay(d.year, months)} ${MONTHS_FULL[months - 1]} ${d.year}`}
          aside={
            <Link
              to="/cockpit"
              className="inline-flex items-center gap-1.5 rounded-sm border border-line bg-surface px-3 py-1.5 text-[13px] font-semibold text-ink no-underline hover:bg-surface-2"
            >
              <ArrowLeft size={16} strokeWidth={1.75} /> Vue par entité
            </Link>
          }
        />
        <Notice icon={<Info size={18} strokeWidth={1.75} />}>
          <strong className="text-ink">Benchmark, pas un résultat de groupe.</strong> La comparaison porte sur
          les entités déjà suivies ({names}). D’autres sociétés du groupe ne sont pas encore intégrées : les
          totaux ne reflètent pas le résultat consolidé réel.
        </Notice>
      </div>

      <section aria-labelledby="tab">
        <SectionHeader
          id="tab"
          title="Tableau comparatif"
          source={
            <>
              <SourceTag source="FIN" date={fin} />
              <SourceTag source="OPE" date={ope} />
            </>
          }
          sub="Trié par CA cumulé. Les valeurs en dessous du budget ou de l’objectif sont signalées."
        />
        <Card padded={false} className="px-2 py-3">
          <Table minWidth={860}>
            <thead>
              <tr>
                <Th>Entité</Th>
                <Th align="right">CA cumulé</Th>
                <Th align="right">CA / budget annuel</Th>
                <Th align="right">Taux de marge brute</Th>
                <Th align="right">REX</Th>
                <Th align="right">Taux de REX</Th>
                <Th align="right">DSO</Th>
                <Th align="right">Commandes / objectif</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const low = (bad: boolean, neg = false) =>
                  cn(bad && (neg ? 'font-bold text-danger-600' : 'font-semibold text-warn-700'));
                return (
                  <tr key={r.key} className="transition-colors duration-fast hover:bg-surface-2">
                    <Td className="font-semibold">
                      <Link
                        to={`/cockpit?ent=${encodeURIComponent(r.key)}`}
                        className="text-ink no-underline hover:text-navy-600 hover:underline"
                      >
                        {r.name}
                      </Link>
                    </Td>
                    <Td align="right" className="font-semibold">
                      {meur(r.ca)}
                    </Td>
                    <Td align="right">{Math.round(r.caPct)} %</Td>
                    <Td align="right" className={low(r.margePct < r.margeBudgetPct)}>
                      {pct(r.margePct)}
                    </Td>
                    <Td align="right" className={low(r.rex < r.rexBudget, r.rex < 0)}>
                      {meur(r.rex)}
                    </Td>
                    <Td align="right" className={low(r.tauxRex < r.tauxRexBudget, r.tauxRex < 0)}>
                      {pct(r.tauxRex)}
                    </Td>
                    <Td align="right" className={low(r.dso != null && r.dso > COCKPIT_RULES.dsoTarget)}>
                      {r.dso != null ? `${Math.round(r.dso)} j` : '—'}
                    </Td>
                    <Td align="right">{r.ordersPct != null ? `${Math.round(r.ordersPct)} %` : '—'}</Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      </section>

      <div className="grid gap-6 xl:grid-cols-2">
        <section aria-labelledby="rex">
          <SectionHeader
            id="rex"
            title="REX par entité"
            sub="Cumul de l’année, repère = budget annuel proratisé au nombre de mois écoulés."
          />
          <Card>
            <HBars
              fmt={meur}
              items={rows.map((r) => ({
                key: r.key,
                label: r.name,
                value: r.rex,
                ref: r.rexBudget,
                good: r.rex >= r.rexBudget,
              }))}
            />
          </Card>
        </section>
        <section aria-labelledby="trex">
          <SectionHeader
            id="trex"
            title="Taux de REX par entité"
            sub="Cumul de l’année, repère = taux budgété de l’entité."
          />
          <Card>
            <HBars
              fmt={(n) => pct(n)}
              items={rows.map((r) => ({
                key: r.key,
                label: r.name,
                value: r.tauxRex,
                ref: r.tauxRexBudget,
                good: r.tauxRex >= r.tauxRexBudget,
              }))}
            />
          </Card>
        </section>
      </div>

      <section aria-labelledby="dsoe">
        <SectionHeader
          id="dsoe"
          title="DSO par entité"
          source={<SourceTag source="OPE" date={ope} />}
          sub={`Dernier mois disponible, objectif ${COCKPIT_RULES.dsoTarget} jours.`}
        />
        <Card className="max-w-[760px]">
          <HBars
            fmt={(n) => `${Math.round(n)} j`}
            refLine={COCKPIT_RULES.dsoTarget}
            refLabel={`objectif ${COCKPIT_RULES.dsoTarget} j`}
            items={rows
              .filter((r) => r.dso != null)
              .map((r) => ({
                key: r.key,
                label: r.name,
                value: r.dso!,
                good: r.dso! <= COCKPIT_RULES.dsoTarget,
              }))}
          />
        </Card>
      </section>
    </div>
  );
}
